if (!customElements.get('mm-bundles-builder')) {
  customElements.define(
    'mm-bundles-builder',
    class MmBundlesBuilder extends HTMLElement {
      constructor() {
        super();
        this.moneyFormat = this.getMoneyFormat();
        this.pendingAddonUpdates = new Set();
      }

      connectedCallback() {
        this.bundleCards = this.querySelectorAll('[data-bundle-card]');
        this.planCards = this.querySelectorAll('[data-plan-card]');
        this.addButton = this.querySelector('[data-add-to-cart]');
        this.bundleErrorEl = this.querySelector('[data-bundle-error]');
        this.subscribePriceEl = this.querySelector('[data-subscribe-price]');
        this.subscribeCompareEl = this.querySelector('[data-subscribe-compare]');
        this.onetimePriceEl = this.querySelector('[data-onetime-price]');
        this.addonTrack = this.querySelector('[data-addon-track]');
        this.addonPrev = this.querySelector('[data-addon-prev]');
        this.addonNext = this.querySelector('[data-addon-next]');

        this.bundleCards.forEach((card) => {
          this.setBundleQuantity(card, 0, { silent: true });
        });

        this.planCards.forEach((card) => {
          const input = card.querySelector('input[type="radio"]');
          input?.addEventListener('change', () => this.onPlanChange(card));
        });

        this.addEventListener('click', (event) => {
          const bundleAddButton = event.target.closest('[data-bundle-add]');
          if (bundleAddButton) {
            event.preventDefault();
            event.stopPropagation();
            this.selectBundle(bundleAddButton.closest('[data-bundle-card]'));
            return;
          }

          const bundleIncrementButton = event.target.closest('[data-bundle-increment]');
          if (bundleIncrementButton) {
            event.preventDefault();
            event.stopPropagation();
            this.changeBundleQuantity(bundleIncrementButton.closest('[data-bundle-card]'), 1);
            return;
          }

          const bundleDecrementButton = event.target.closest('[data-bundle-decrement]');
          if (bundleDecrementButton) {
            event.preventDefault();
            event.stopPropagation();
            this.changeBundleQuantity(bundleDecrementButton.closest('[data-bundle-card]'), -1);
            return;
          }

          const addButton = event.target.closest('[data-addon-add]');
          if (addButton) {
            event.preventDefault();
            event.stopPropagation();
            this.addAddonToCart(addButton.closest('[data-addon-card]'), addButton);
            return;
          }

          const incrementButton = event.target.closest('[data-addon-increment]');
          if (incrementButton) {
            event.preventDefault();
            event.stopPropagation();
            this.changeAddonQuantity(incrementButton.closest('[data-addon-card]'), 1, incrementButton);
            return;
          }

          const decrementButton = event.target.closest('[data-addon-decrement]');
          if (decrementButton) {
            event.preventDefault();
            event.stopPropagation();
            this.changeAddonQuantity(decrementButton.closest('[data-addon-card]'), -1, decrementButton);
          }
        });

        this.addButton?.addEventListener('click', () => this.addToCart());
        this.addonPrev?.addEventListener('click', () => this.scrollAddons(-1));
        this.addonNext?.addEventListener('click', () => this.scrollAddons(1));
        this.addonTrack?.addEventListener('scroll', () => this.updateAddonNavState(), { passive: true });

        if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            if (event.source === 'mm-bundles') return;
            this.syncAddonQuantitiesFromCart();
          });
        }

        if (this.addonTrack && typeof ResizeObserver !== 'undefined') {
          this.addonResizeObserver = new ResizeObserver(() => this.updateAddonNavState());
          this.addonResizeObserver.observe(this.addonTrack);
        }

        this.updateAddButtonState();
        this.updateAddonNavState();
        this.updateBundleCardPrices();
      }

      disconnectedCallback() {
        this.cartUpdateUnsubscriber?.();
        this.addonResizeObserver?.disconnect();
      }

      getMoneyFormat() {
        if (window.Shopify?.formatMoney) {
          return (cents) => window.Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
        }
        return (cents) => `$${(cents / 100).toFixed(2)}`;
      }

      formatMoney(cents) {
        return this.moneyFormat(Number(cents) || 0);
      }

      getSelectedBundleCards() {
        return [...this.bundleCards].filter((card) => this.getBundleQuantity(card) > 0);
      }

      getSelectedBundleCard() {
        return this.getSelectedBundleCards()[0] || null;
      }

      hasSelectedBundles() {
        return this.getSelectedBundleCards().length > 0;
      }

      getBundleQuantity(card) {
        const valueEl = card?.querySelector('[data-bundle-quantity]');
        return Number(valueEl?.textContent) || 0;
      }

      setBundleQuantity(card, quantity, { silent = false } = {}) {
        if (!card) return;

        const qty = Math.max(0, Number(quantity) || 0);
        const valueEl = card.querySelector('[data-bundle-quantity]');
        if (valueEl) valueEl.textContent = String(qty);

        if (qty > 0) {
          this.showBundleQuantity(card);
        } else {
          this.showBundleAddButton(card);
        }

        if (!silent) {
          this.updateAddButtonState();
          if (this.hasSelectedBundles()) {
            this.updatePricing();
          } else {
            this.clearPricing();
          }
        }
      }

      showBundleQuantity(card) {
        card.classList.add('mm-bundles__bundle-card--selected');
        card.querySelector('[data-bundle-add]')?.classList.add('hidden');
        card.querySelector('[data-bundle-qty]')?.classList.remove('hidden');
      }

      showBundleAddButton(card) {
        card.classList.remove('mm-bundles__bundle-card--selected');
        if (card.dataset.available === 'false') return;

        card.querySelector('[data-bundle-qty]')?.classList.add('hidden');
        card.querySelector('[data-bundle-add]')?.classList.remove('hidden');
      }

      selectBundle(card) {
        if (!card || card.dataset.available === 'false') return;

        this.setBundleQuantity(card, 1);
        this.clearBundleError();
      }

      changeBundleQuantity(card, delta) {
        if (!card || card.dataset.available === 'false') return;

        const currentQty = this.getBundleQuantity(card);
        if (currentQty <= 0 && delta > 0) {
          this.setBundleQuantity(card, 1);
          this.clearBundleError();
          return;
        }

        const nextQty = currentQty + delta;
        if (nextQty < 0) return;

        this.setBundleQuantity(card, nextQty);
        this.clearBundleError();
      }

      updateAddButtonState() {
        if (!this.addButton) return;
        this.addButton.disabled = !this.hasSelectedBundles();
      }

      getSelectedPlan() {
        return this.querySelector('[data-plan-card] input[type="radio"]:checked')?.value || 'subscribe';
      }

      onPlanChange(card) {
        this.planCards.forEach((item) => {
          item.classList.toggle('mm-bundles__plan-card--selected', item === card);
        });
        this.clearBundleError();
        this.updateBundleCardPrices();
        if (this.hasSelectedBundles()) {
          this.updatePricing();
        }
      }

      updateBundleCardPrices() {
        const plan = this.getSelectedPlan();

        this.bundleCards.forEach((card) => {
          const priceEl = card.querySelector('[data-bundle-price]');
          if (!priceEl) return;

          const unitPrice = Number(card.dataset.price) || 0;
          const unitSubscribe = Number(card.dataset.sellingPlanPrice) || unitPrice;
          const displayPrice = plan === 'subscribe' ? unitSubscribe : unitPrice;

          priceEl.textContent = this.formatMoney(displayPrice);
        });
      }

      updatePricing() {
        const selectedCards = this.getSelectedBundleCards();
        if (!selectedCards.length) {
          this.clearPricing();
          return;
        }

        let onetimeTotal = 0;
        let subscribeTotal = 0;
        let subscribeCompareTotal = 0;

        selectedCards.forEach((card) => {
          const quantity = this.getBundleQuantity(card);
          const unitPrice = Number(card.dataset.price) || 0;
          const unitCompare = Number(card.dataset.comparePrice) || unitPrice;
          const unitSubscribe = Number(card.dataset.sellingPlanPrice) || unitPrice;
          const unitSubscribeCompare = unitCompare > unitSubscribe ? unitCompare : unitPrice;

          onetimeTotal += unitPrice * quantity;
          subscribeTotal += unitSubscribe * quantity;
          subscribeCompareTotal += unitSubscribeCompare * quantity;
        });

        if (this.onetimePriceEl) {
          this.onetimePriceEl.textContent = this.formatMoney(onetimeTotal);
        }

        if (this.subscribePriceEl) {
          this.subscribePriceEl.textContent = this.formatMoney(subscribeTotal);
        }

        if (this.subscribeCompareEl) {
          if (subscribeCompareTotal > subscribeTotal) {
            this.subscribeCompareEl.textContent = this.formatMoney(subscribeCompareTotal);
          } else {
            this.subscribeCompareEl.textContent = '';
          }
        }
      }

      clearPricing() {
        if (this.onetimePriceEl) this.onetimePriceEl.textContent = '—';
        if (this.subscribePriceEl) this.subscribePriceEl.textContent = '—';
        if (this.subscribeCompareEl) this.subscribeCompareEl.textContent = '';
      }

      getAddonQuantity(card) {
        const valueEl = card?.querySelector('[data-addon-quantity]');
        return Number(valueEl?.textContent) || 0;
      }

      setAddonQuantity(card, quantity) {
        const qty = Math.max(0, Number(quantity) || 0);
        const valueEl = card.querySelector('[data-addon-quantity]');
        if (valueEl) valueEl.textContent = String(qty);

        if (qty > 0) {
          this.showAddonQuantity(card);
        } else {
          this.showAddonAddButton(card);
        }
      }

      showAddonQuantity(card) {
        card.classList.add('mm-bundles__addon-card--selected');
        card.querySelector('[data-addon-add]')?.classList.add('hidden');
        card.querySelector('[data-addon-qty]')?.classList.remove('hidden');
      }

      showAddonAddButton(card) {
        card.classList.remove('mm-bundles__addon-card--selected');
        if (card.dataset.available === 'false') return;

        card.querySelector('[data-addon-qty]')?.classList.add('hidden');
        const addButton = card.querySelector('[data-addon-add]');
        addButton?.classList.remove('hidden');
        if (addButton) addButton.disabled = false;
      }

      setAddonActionsDisabled(card, disabled) {
        card.querySelectorAll('[data-addon-add], [data-addon-decrement], [data-addon-increment]').forEach((button) => {
          button.disabled = disabled;
        });
      }

      isAddonPending(card) {
        return this.pendingAddonUpdates.has(card.dataset.variantId);
      }

      async addAddonToCart(card, button) {
        if (!card || this.isAddonPending(card) || this.getAddonQuantity(card) > 0) return;
        if (card.dataset.available === 'false') return;

        const variantId = card.dataset.variantId;
        if (!variantId) return;

        this.pendingAddonUpdates.add(variantId);
        this.setAddonActionsDisabled(card, true);
        this.clearAddonError(card);

        try {
          const { response, data, cart } = await this.postItemsToCart(
            [{ id: Number(variantId), quantity: 1 }],
            { activeElement: button }
          );

          if (!response.ok || this.isCartError(data)) {
            this.showAddonError(card, this.getCartErrorMessage(data, 'add'));
            return;
          }

          this.setAddonQuantity(card, 1);
          await this.handleCartSuccess(data, cart, { addonCard: card, showNotification: true });
        } catch (error) {
          this.showAddonError(card, 'Unable to add to cart. Please try again.');
        } finally {
          this.pendingAddonUpdates.delete(variantId);
          this.setAddonActionsDisabled(card, false);
        }
      }

      async changeAddonQuantity(card, delta, button) {
        if (!card || this.isAddonPending(card)) return;
        if (delta > 0 && card.dataset.available === 'false') return;

        const variantId = card.dataset.variantId;
        if (!variantId) return;

        const currentQty = this.getAddonQuantity(card);
        const nextQty = currentQty + delta;
        if (nextQty < 0) return;

        this.pendingAddonUpdates.add(variantId);
        this.setAddonActionsDisabled(card, true);
        this.clearAddonError(card);

        try {
          const result =
            delta > 0
              ? await this.postItemsToCart([{ id: Number(variantId), quantity: delta }], { activeElement: button })
              : await this.postCartUpdate({ [variantId]: nextQty }, { activeElement: button });

          const { response, data, cart } = result;

          if (!response.ok || this.isCartError(data)) {
            this.showAddonError(card, this.getCartErrorMessage(data, delta > 0 ? 'add' : 'update'));
            return;
          }

          this.setAddonQuantity(card, nextQty);
          await this.handleCartSuccess(data, cart, { addonCard: card, showNotification: delta > 0 });
        } catch (error) {
          this.showAddonError(
            card,
            delta > 0 ? 'Unable to add to cart. Please try again.' : 'Unable to update cart. Please try again.'
          );
        } finally {
          this.pendingAddonUpdates.delete(variantId);
          this.setAddonActionsDisabled(card, false);
        }
      }

      async syncAddonQuantitiesFromCart() {
        try {
          const response = await fetch(`${window.routes?.cart_url || '/cart'}.js`);
          if (!response.ok) return;

          const cart = await response.json();
          this.querySelectorAll('[data-addon-card]').forEach((card) => {
            const variantId = Number(card.dataset.variantId);
            const item = cart.items.find((lineItem) => lineItem.variant_id === variantId);
            this.setAddonQuantity(card, item ? item.quantity : 0);
          });
        } catch (error) {
          // Ignore sync errors; local state remains usable.
        }
      }

      scrollAddons(direction) {
        if (!this.addonTrack) return;

        const navButton = direction < 0 ? this.addonPrev : this.addonNext;
        if (navButton?.disabled) return;

        const card = this.addonTrack.querySelector('[data-addon-card]');
        const cardWidth = card ? card.offsetWidth : 0;
        const gap = parseFloat(getComputedStyle(this.addonTrack).columnGap || getComputedStyle(this.addonTrack).gap) || 16;
        const amount = direction * (cardWidth + gap);
        this.addonTrack.scrollBy({ left: amount, behavior: 'smooth' });
      }

      updateAddonNavState() {
        if (!this.addonTrack || !this.addonPrev || !this.addonNext) return;

        const canScroll = this.addonTrack.scrollWidth > this.addonTrack.clientWidth + 1;

        if (!canScroll) {
          this.addonPrev.disabled = true;
          this.addonNext.disabled = true;
          return;
        }

        const atStart = this.addonTrack.scrollLeft <= 1;
        const atEnd =
          this.addonTrack.scrollLeft + this.addonTrack.clientWidth >= this.addonTrack.scrollWidth - 1;

        this.addonPrev.disabled = atStart;
        this.addonNext.disabled = atEnd;
      }

      isCartError(data) {
        return Boolean(data?.status && data.status !== 200);
      }

      getCartErrorMessage(data, action = 'update') {
        const fallback = action === 'add' ? 'Unable to add to cart.' : 'Unable to update cart.';
        return data?.description || data?.message || fallback;
      }

      normalizeCartAddResponse(data) {
        if (!data || typeof data !== 'object') return data;

        const key = data.key || data.items?.[0]?.key;
        if (!key || data.key) return data;

        return { ...data, key };
      }

      getCartSections(cart) {
        const sections = new Set(['cart-icon-bubble']);

        if (cart && typeof cart.getSectionsToRender === 'function') {
          cart.getSectionsToRender().forEach((section) => {
            const name = section.section || section.id;
            if (name) sections.add(name);
          });
        } else {
          sections.add('cart-drawer');
        }

        return [...sections];
      }

      getSectionInnerHTML(html, selector = '.shopify-section') {
        if (!html) return '';

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const element = doc.querySelector(selector) || doc.querySelector('.shopify-section');
        return element?.innerHTML || '';
      }

      async updateCartIconBubble(sectionHtml) {
        const cartIcon = document.getElementById('cart-icon-bubble');
        if (!cartIcon) return;

        if (sectionHtml) {
          const innerHTML = this.getSectionInnerHTML(sectionHtml);
          if (innerHTML) {
            cartIcon.innerHTML = innerHTML;
            return;
          }
        }

        try {
          const response = await fetch(`${window.location.pathname}?section_id=cart-icon-bubble`);
          if (!response.ok) return;

          const html = await response.text();
          const innerHTML = this.getSectionInnerHTML(html);
          if (innerHTML) cartIcon.innerHTML = innerHTML;
        } catch (error) {
          console.error(error);
        }
      }

      updateCartDrawer(cart, sectionHtml) {
        if (!cart || !sectionHtml) return;

        try {
          const drawer = document.querySelector('#CartDrawer');
          if (drawer) {
            const innerHTML = this.getSectionInnerHTML(sectionHtml, '#CartDrawer');
            if (innerHTML) drawer.innerHTML = innerHTML;
          }

          cart.querySelector('.drawer__inner')?.classList.remove('is-empty');
        } catch (error) {
          console.error(error);
        }
      }

      clearBundleError() {
        if (this.bundleErrorEl) {
          this.bundleErrorEl.textContent = '';
          this.bundleErrorEl.classList.add('hidden');
        }
      }

      showBundleError(message) {
        if (this.bundleErrorEl) {
          this.bundleErrorEl.textContent = message;
          this.bundleErrorEl.classList.remove('hidden');
        }
      }

      clearAddonError(card) {
        const errorEl = card?.querySelector('[data-addon-error]');
        if (!errorEl) return;
        errorEl.textContent = '';
        errorEl.classList.add('hidden');
      }

      showAddonError(card, message) {
        const errorEl = card?.querySelector('[data-addon-error]');
        if (!errorEl) return;
        errorEl.textContent = message;
        errorEl.classList.remove('hidden');
      }

      async postFormItemToCart(item, { activeElement, cart } = {}) {
        const cartElement = cart || document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const sections = this.getCartSections(cartElement);
        const formData = new FormData();
        formData.append('id', item.id);
        formData.append('quantity', item.quantity);

        if (sections?.length) {
          formData.append('sections', sections.join(','));
          formData.append('sections_url', window.location.pathname);
        }

        if (cartElement && activeElement && typeof cartElement.setActiveElement === 'function') {
          cartElement.setActiveElement(activeElement);
        }

        const response = await fetch(`${window.routes?.cart_add_url || '/cart/add'}`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: formData,
        });

        const data = this.normalizeCartAddResponse(await response.json());
        return { response, data, cart: cartElement };
      }

      async postItemsToCart(items, { activeElement } = {}) {
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        if (items.length === 1 && !items[0].selling_plan) {
          return this.postFormItemToCart(items[0], { activeElement, cart });
        }

        const payload = { items };
        const sections = this.getCartSections(cart);

        if (sections) {
          payload.sections = sections.join(',');
          payload.sections_url = window.location.pathname;
        }

        if (cart && activeElement && typeof cart.setActiveElement === 'function') {
          cart.setActiveElement(activeElement);
        }

        const response = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(payload),
        });

        const data = this.normalizeCartAddResponse(await response.json());
        return { response, data, cart };
      }

      async postCartUpdate(updates, { activeElement } = {}) {
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const payload = { updates };
        const sections = this.getCartSections(cart);

        if (sections) {
          payload.sections = sections.join(',');
          payload.sections_url = window.location.pathname;
        }

        if (cart && activeElement && typeof cart.setActiveElement === 'function') {
          cart.setActiveElement(activeElement);
        }

        const response = await fetch(`${window.routes?.cart_update_url || '/cart/update'}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(payload),
        });

        const data = await response.json();
        return { response, data, cart };
      }

      async handleCartSuccess(data, cart, { addonCard, showNotification = true } = {}) {
        this.clearBundleError();
        if (addonCard) this.clearAddonError(addonCard);

        const cartData = this.normalizeCartAddResponse(data);

        await this.updateCartIconBubble(cartData.sections?.['cart-icon-bubble']);

        if (typeof publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
          await publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'mm-bundles',
            cartData,
          });
        }

        const isNotification = cart?.tagName === 'CART-NOTIFICATION';

        if (cart && showNotification && cartData.sections && typeof cart.renderContents === 'function') {
          try {
            cart.renderContents(cartData);
          } catch (error) {
            console.error(error);

            if (!isNotification && cartData.sections['cart-drawer']) {
              this.updateCartDrawer(cart, cartData.sections['cart-drawer']);
            }
          }
        } else if (cart && !isNotification && cartData.sections?.['cart-drawer']) {
          this.updateCartDrawer(cart, cartData.sections['cart-drawer']);
        } else if (!cart) {
          window.location.href = window.routes?.cart_url || '/cart';
        }

        if (cart?.classList.contains('is-empty')) {
          cart.classList.remove('is-empty');
        }
      }

      async addToCart() {
        const selectedCards = this.getSelectedBundleCards();
        const plan = this.getSelectedPlan();

        const items = selectedCards
          .map((card) => {
            const variantId = card.dataset.variantId;
            const quantity = this.getBundleQuantity(card);
            if (!variantId || quantity < 1) return null;

            const item = {
              id: Number(variantId),
              quantity,
            };

            if (plan === 'subscribe' && card.dataset.sellingPlanId) {
              item.selling_plan = Number(card.dataset.sellingPlanId);
            }

            return item;
          })
          .filter(Boolean);

        if (!items.length) {
          this.showBundleError('Please select a bundle.');
          return;
        }

        this.addButton.disabled = true;

        try {
          const { response, data, cart } = await this.postItemsToCart(items, {
            activeElement: this.addButton,
          });

          if (!response.ok || this.isCartError(data)) {
            this.showBundleError(this.getCartErrorMessage(data, 'add'));
            return;
          }

          await this.handleCartSuccess(data, cart);
        } catch (error) {
          this.showBundleError('Unable to add to cart. Please try again.');
        } finally {
          this.updateAddButtonState();
        }
      }
    }
  );
}
